import axios, { AxiosError } from 'axios'
import { serverUrl } from '../Constants'

/**
 * Cliente tipado para los reportes de Gestión encolados (`v3/gestion/reports`).
 * El worker genera el Excel, lo envía por correo y lo deja disponible 48 h.
 */

export type ReportKey =
    | 'catalog'
    | 'exchanges'
    | 'shoppingBook'
    | 'costOfSale'
    | 'pendings'
    | 'quadrature'
    | 'correlatives'
    | 'processInventories'
    | 'foreignVendors'

export type ReportStatus = 'pending' | 'processing' | 'done' | 'failed' | 'expired'

export type ReportRequest = {
    id: number
    report: ReportKey | string
    report_label: string
    params: Record<string, any> | null
    status: ReportStatus
    file_name: string | null
    file_size: number | null
    email: string | null
    emailed: boolean
    error: string | null
    created_at: string
    started_at: string | null
    finished_at: string | null
    expires_at: string | null
}

type ApiResponse<T> = {
    success: boolean
    data: T
    message?: string
}

const BASE = `${serverUrl}v3/gestion/reports`

const authHeaders = (token: string) => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`
})

export class ReportApiError extends Error {
    status: number

    constructor(status: number, message: string) {
        super(message)
        this.status = status
        // Necesario con target es5 para que instanceof funcione.
        Object.setPrototypeOf(this, ReportApiError.prototype)
    }
}

/** Lee el `message` de un body que puede venir como blob (descargas). */
async function extractMessage(data: any): Promise<string | undefined> {
    if (!data) return undefined
    if (typeof Blob !== 'undefined' && data instanceof Blob) {
        try {
            const text = await new Response(data).text()
            return JSON.parse(text)?.message
        } catch (e) {
            return undefined
        }
    }
    return data?.message
}

async function toApiError(err: AxiosError): Promise<ReportApiError> {
    if (!err.response) {
        return new ReportApiError(0, 'No se pudo conectar con el servidor. Revisa tu conexión e intenta nuevamente.')
    }
    const { status, statusText, data } = err.response
    const serverMessage = await extractMessage(data)
    switch (status) {
        case 401:
            return new ReportApiError(status, 'Tu sesión expiró. Vuelve a iniciar sesión.')
        case 404:
            return new ReportApiError(status, serverMessage || 'El reporte no existe o no te pertenece.')
        case 410:
            return new ReportApiError(status, serverMessage || 'El archivo expiró (disponible solo 48 h). Solicita el reporte nuevamente.')
        case 422:
            return new ReportApiError(status, serverMessage || 'La solicitud no es válida. Revisa los parámetros del reporte.')
        default:
            return new ReportApiError(status, serverMessage || `Error del servidor (${status}${statusText ? ` ${statusText}` : ''}).`)
    }
}

async function wrap<T>(promise: Promise<T>): Promise<T> {
    try {
        return await promise
    } catch (err) {
        throw await toApiError(err as AxiosError)
    }
}

/** POST v3/gestion/reports — encola un reporte (202). */
export async function enqueueReport(token: string, report: ReportKey, params: Record<string, any> = {}): Promise<ReportRequest> {
    const res = await wrap(axios.post<ApiResponse<ReportRequest>>(BASE, { report, params }, { headers: authHeaders(token) }))
    if (!res.data?.success) {
        throw new ReportApiError(res.status, res.data?.message || 'No se pudo encolar el reporte.')
    }
    return res.data.data
}

/** GET v3/gestion/reports — historial del usuario autenticado. */
export async function listReports(token: string): Promise<ReportRequest[]> {
    const res = await wrap(axios.get<ApiResponse<ReportRequest[]>>(BASE, { headers: authHeaders(token) }))
    return Array.isArray(res.data?.data) ? res.data.data : []
}

/** Extrae el nombre de archivo de un Content-Disposition, si viene. */
function fileNameFromDisposition(disposition?: string): string | undefined {
    if (!disposition) return undefined
    const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(disposition)
    if (utf8) {
        try {
            return decodeURIComponent(utf8[1].trim())
        } catch (e) { /* sigue con filename= */ }
    }
    const plain = /filename="?([^";]+)"?/i.exec(disposition)
    return plain ? plain[1].trim() : undefined
}

/** GET v3/gestion/reports/{id}/download — descarga el xlsx como blob y lo guarda. */
export async function downloadReport(token: string, request: Pick<ReportRequest, 'id' | 'file_name'>): Promise<void> {
    const res = await wrap(axios.get(`${BASE}/${request.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
    }))
    const fileName = request.file_name
        || fileNameFromDisposition(res.headers?.['content-disposition'])
        || `reporte-${request.id}.xlsx`
    const blob = new Blob([res.data], {
        type: res.headers?.['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    })

    const nav: any = window.navigator
    if (nav.msSaveOrOpenBlob) {
        nav.msSaveOrOpenBlob(blob, fileName)
        return
    }
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', fileName)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.setTimeout(() => window.URL.revokeObjectURL(url), 1000)
}

/** DELETE v3/gestion/reports/{id} — elimina la solicitud y su archivo. */
export async function deleteReport(token: string, id: number): Promise<void> {
    await wrap(axios.delete(`${BASE}/${id}`, { headers: authHeaders(token) }))
}
